import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Per-field tracker for in-flight pre-order header updates. Cells call
 * `track(keys, mutate, expectedValues?)` to bump a per-field refcount and
 * record the value they're trying to write; `isPending(field, currentValue?)`
 * stays true until BOTH every in-flight mutation has returned AND the
 * rendered prop value matches the expected one (or has at least visibly
 * moved off the pre-edit snapshot).
 *
 * Why value-based rather than just "settled": the V2 mutation resolves the
 * moment the Dexie write returns (see `apps/web/.../usePreOrderMutations.ts`
 * — `updatePreOrder` awaits the storage put, then fires the V2 push
 * fire-and-forget). The visible cell value is driven by `useLiveQuery`,
 * which re-renders 1–2 frames later. Clearing on `onSettled` produces a
 * brief "spinner gone, value unchanged" flicker that reads as the edit
 * being dropped. Holding the spinner until `currentValue === expected`
 * guarantees the user only sees the indicator clear once the cell has
 * actually repainted with the new value.
 *
 * Overlapping edits: every `track()` increments `inFlightCount`; `onSettled`
 * decrements it. The value-match release path runs only when
 * `inFlightCount === 0`, so a second edit for the same field can't be marked
 * settled when the first mutation's `onSettled` lands.
 *
 * Safety: a 5 s grace timeout fires after every in-flight mutation returns,
 * so the spinner can't get stuck forever if the rendered value never matches
 * expected (server rejection, partner switch that unmounts the row, etc).
 * The timeout is cancelled both on early `match-expected` release and at the
 * top of every fresh `track()` for the same field, so stale timers can't
 * fire 5 s later and wipe a newer pending edit's state.
 */
const UNSET = Symbol("unset");

type Entry = {
  /** Total pending markers (track + manual begin/release). > 0 ⇒ visible. */
  count: number;
  /**
   * Subset of `count` representing mutations not yet returned from `onSettled`.
   * Separate from `count` so an overlapping second `track()` for the same field
   * doesn't have its still-in-flight mutation marked settled when the first
   * mutation's `onSettled` fires — `inFlightCount > 0` keeps `isPending` true
   * regardless of whether the rendered value has caught up to an older expected.
   */
  inFlightCount: number;
  expected: unknown;
  hasExpected: boolean;
  /**
   * Value the cell was rendering at the moment `track(...)` ran. Captured on
   * the first `isPending(field, currentValue)` call after track so the hook
   * can detect "the prop changed from whatever it was before" — needed when
   * the cell's source object swaps under us (e.g. a date edit reorders the
   * partner card and `preOrders[0]` is now a different pre-order). In that
   * case `currentValue` will never equal `expected`, but it WILL differ from
   * `priorValue`, which is enough to know the rendered cell has moved on.
   */
  priorValue: unknown;
  hasPriorValue: boolean;
  /** Set when an `isPending`-triggered rAF release is in flight; guards against duplicate schedules. */
  releaseScheduled: boolean;
  /** Safety timeout id so an early release cancels the redundant 5 s clear. */
  safetyTimeoutId: ReturnType<typeof setTimeout> | null;
};

export type PendingFieldsApi = {
  /**
   * `currentValue` is the cell's currently rendered value for `field`. When
   * an expected value was recorded via `track(..., expectedValues)`, the
   * indicator stays on until `currentValue === expected`. Omit for the
   * legacy settled-only behaviour (used as a fallback).
   */
  isPending: (field: string, currentValue?: unknown) => boolean;
  /**
   * Run `mutate` while marking `fields` as pending. `expectedValues` keys the
   * value each field is being moved to so `isPending` can hold the spinner
   * until the rendered prop catches up. Omit a key when the prop you'd
   * compare against doesn't map 1:1 to what was written (e.g. nested
   * metadata) — the field falls back to clearing on settle alone.
   */
  track: (
    fields: readonly string[],
    mutate: (onSettled: () => void) => void,
    expectedValues?: Readonly<Record<string, unknown>>,
  ) => void;
  /** Manually bump pending counts — pair with `release(...)` in a `finally`. */
  begin: (fields: readonly string[]) => void;
  release: (fields: readonly string[]) => void;
};

/**
 * Hard ceiling on how long a single field can stay pending after `onSettled`.
 * If the rendered value never matches expected (server rejected the write,
 * cell unmounted before SSE arrived, network died), we clear anyway so the
 * user isn't left with a permanent spinner.
 */
const SAFETY_TIMEOUT_MS = 5000;

export function usePendingHeaderFields(): PendingFieldsApi {
  // Per-field state lives in a ref so the callbacks are stable; a separate
  // version counter triggers a re-render when something visible changes.
  const entriesRef = useRef(new Map<string, Entry>());
  const [, setVersion] = useState(0);
  const bumpVersion = useCallback(() => setVersion((v) => v + 1), []);
  // Track outstanding safety timeouts so unmount doesn't leak a clear into
  // a tracker the host already threw away.
  const safetyTimeoutsRef = useRef(new Set<ReturnType<typeof setTimeout>>());
  useEffect(() => {
    return () => {
      for (const id of safetyTimeoutsRef.current) clearTimeout(id);
      safetyTimeoutsRef.current.clear();
    };
  }, []);

  const ensureEntry = (field: string): Entry => {
    let entry = entriesRef.current.get(field);
    if (!entry) {
      entry = {
        count: 0,
        inFlightCount: 0,
        expected: UNSET,
        hasExpected: false,
        priorValue: UNSET,
        hasPriorValue: false,
        releaseScheduled: false,
        safetyTimeoutId: null,
      };
      entriesRef.current.set(field, entry);
    }
    return entry;
  };

  /**
   * Clear any pending safety timeout owned by this entry. Called both when a
   * fresh `track()` lands (we're "in flight" again, no need to safety-clear)
   * and from `fullyClear` (we're about to delete the entry anyway). Without
   * it, a stale timeout from a prior settle can fire 5 s later and clobber a
   * newer pending edit's state.
   */
  const cancelSafetyTimeout = (entry: Entry) => {
    if (entry.safetyTimeoutId !== null) {
      clearTimeout(entry.safetyTimeoutId);
      safetyTimeoutsRef.current.delete(entry.safetyTimeoutId);
      entry.safetyTimeoutId = null;
    }
  };

  const fullyClear = useCallback(
    (fields: readonly string[]) => {
      let changed = false;
      for (const field of fields) {
        const entry = entriesRef.current.get(field);
        if (!entry) continue;
        cancelSafetyTimeout(entry);
        entriesRef.current.delete(field);
        changed = true;
      }
      if (changed) bumpVersion();
    },
    [bumpVersion],
  );

  const begin = useCallback(
    (fields: readonly string[]) => {
      for (const field of fields) {
        const entry = ensureEntry(field);
        entry.count += 1;
        entry.releaseScheduled = false;
      }
      bumpVersion();
    },
    [bumpVersion],
  );

  const release = useCallback(
    (fields: readonly string[]) => {
      let changed = false;
      for (const field of fields) {
        const entry = entriesRef.current.get(field);
        if (!entry) continue;
        entry.count -= 1;
        if (entry.count <= 0 && entry.inFlightCount <= 0) {
          cancelSafetyTimeout(entry);
          entriesRef.current.delete(field);
        }
        changed = true;
      }
      if (changed) bumpVersion();
    },
    [bumpVersion],
  );

  const track = useCallback<PendingFieldsApi["track"]>(
    (fields, mutate, expectedValues) => {
      for (const field of fields) {
        const entry = ensureEntry(field);
        // Cancel any pending safety timeout left over from a prior `track()`
        // on this field. Without this, if the prior mutation settled but the
        // rendered value never matched (server rejection, slow SSE), its
        // 5 s timer would later fire `fullyClear` and wipe out the new
        // edit's state mid-flight, prematurely hiding the second spinner.
        cancelSafetyTimeout(entry);
        entry.count += 1;
        entry.inFlightCount += 1;
        entry.releaseScheduled = false;
        // Reset the prior-value snapshot so the next `isPending` call captures
        // the post-write current value as the new "before" — chained edits
        // shouldn't compare against an even-older value.
        entry.priorValue = UNSET;
        entry.hasPriorValue = false;
        if (
          expectedValues &&
          Object.prototype.hasOwnProperty.call(expectedValues, field)
        ) {
          // Latest write wins on overlapping edits — when the rendered value
          // hits the most recent expected we clear all stacked counts at once
          // (handled in `isPending`). For a single in-flight edit this is a
          // no-op.
          entry.expected = expectedValues[field];
          entry.hasExpected = true;
        } else {
          entry.expected = UNSET;
          entry.hasExpected = false;
        }
      }
      bumpVersion();

      let onSettledFired = false;
      const onSettled = () => {
        if (onSettledFired) return;
        onSettledFired = true;
        // Fields whose in-flight count just hit zero — only these are
        // eligible for the safety timer + value-match release. Overlapping
        // edits that still have a later mutation outstanding stay locked
        // pending until THEIR `onSettled` lands, no matter how the rendered
        // value moves.
        const fullySettledFields: string[] = [];
        for (const field of fields) {
          const entry = entriesRef.current.get(field);
          if (!entry) continue;
          entry.inFlightCount = Math.max(0, entry.inFlightCount - 1);
          if (entry.inFlightCount === 0) fullySettledFields.push(field);
        }
        bumpVersion();
        if (fullySettledFields.length === 0) return;

        // Safety: clear after the grace period regardless of whether the
        // rendered value caught up. Without this a server rejection or a
        // partner-card unmount mid-flight would strand the spinner. The
        // timeout is also cleared by `fullyClear` (early `match-expected`
        // release) and by the next `track()` for the same field, so it
        // never fires redundantly.
        const timeoutId = setTimeout(() => {
          safetyTimeoutsRef.current.delete(timeoutId);
          for (const field of fullySettledFields) {
            const entry = entriesRef.current.get(field);
            if (entry) entry.safetyTimeoutId = null;
          }
          fullyClear(fullySettledFields);
        }, SAFETY_TIMEOUT_MS);
        safetyTimeoutsRef.current.add(timeoutId);
        for (const field of fullySettledFields) {
          const entry = entriesRef.current.get(field);
          if (entry) entry.safetyTimeoutId = timeoutId;
        }
      };

      try {
        mutate(onSettled);
      } catch (err) {
        // Synchronous failure: settle + clear immediately so the user isn't
        // left staring at a spinner that will never resolve.
        onSettled();
        fullyClear(fields);
        throw err;
      }
    },
    [bumpVersion, fullyClear],
  );

  const scheduleRelease = useCallback(
    (entry: Entry, field: string) => {
      if (entry.releaseScheduled) return;
      entry.releaseScheduled = true;
      // rAF (not microtask) so React doesn't batch the state change into the
      // same paint and elide the confirmation frame — the user sees one paint
      // with the new value AND the spinner before it clears.
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => fullyClear([field]));
      } else {
        // SSR / jsdom fallback — clear straight away. The confirmation frame
        // is a frontend nicety, not a correctness need.
        fullyClear([field]);
      }
    },
    [fullyClear],
  );

  const isPending = useCallback(
    (field: string, currentValue?: unknown) => {
      const entry = entriesRef.current.get(field);
      if (!entry || entry.count === 0) return false;

      // Capture the value the cell was rendering at first read after track.
      // This is the "before" snapshot used as a fallback when the rendered
      // prop diverges from `expected` (e.g. date edit reorders the partner
      // card so `preOrders[0]` now points at a different pre-order, or the
      // stored format doesn't match what was written verbatim).
      if (!entry.hasPriorValue && currentValue !== undefined) {
        entry.priorValue = currentValue;
        entry.hasPriorValue = true;
      }

      // Any mutation still in flight (overlapping edit, slow server) — keep
      // the spinner regardless of how the rendered prop moves. Otherwise an
      // older expected value matching mid-flight would clear the spinner
      // while a newer mutation is still resolving.
      if (entry.inFlightCount > 0) return true;

      // All in-flight mutations have returned. Two ways the rendered cell
      // has caught up:
      //   1. exact match against the expected value (1:1 prop case), or
      //   2. the prop has visibly changed from the pre-edit snapshot — covers
      //      reorders / format mismatches / cell unmount-replace patterns
      //      where the cell's source moved on but doesn't equal `expected`.
      const matchesExpected =
        entry.hasExpected && currentValue === entry.expected;
      const changedFromPrior =
        entry.hasPriorValue && currentValue !== entry.priorValue;

      if (matchesExpected || changedFromPrior) {
        scheduleRelease(entry, field);
        return true;
      }

      // Settled, no expected was set, and the value hasn't visibly changed —
      // likely a same-value write or a metadata-only edit. Hold one frame
      // (matches the no-expected fallback behaviour) so the spinner doesn't
      // blink off the moment the Dexie write returns.
      if (!entry.hasExpected) {
        scheduleRelease(entry, field);
        return true;
      }

      // Settled, expected was set, but the rendered value hasn't caught up
      // yet AND hasn't drifted from the snapshot. Keep the spinner — the
      // safety timeout will clear it if the value never arrives.
      return true;
    },
    [scheduleRelease],
  );

  return { isPending, track, begin, release };
}
